import {
  type TDynamicData,
  type TElementOpacity,
  type TObjectState,
} from '@accure/siteview';
import { compact, flatMap, flow, getOr, identity, map, over } from 'lodash/fp';

import { defaultColor } from '@/siteview/colors/default-color';
import { imbalanceSocColor } from '@/siteview/colors/imbalance-soc';
import { temperatureColor } from '@/siteview/colors/temperature-color';
import { type Asset, assetSocOffset } from '@/siteview/entities/asset';
import { AssetType } from '@/siteview/entities/asset-type';
import type { AssetsFilters } from '@/siteview/entities/assets-filters';
import type { Settings } from '@/siteview/entities/settings';
import type { StaticData } from '@/siteview/entities/static-data';
import {
  type ViewFilters,
  ViewFiltersMode,
} from '@/siteview/entities/view-filters';
import {
  ACCURE_SOC_RANGE,
  BMS_SOC_RANGE,
  IMBALANCE_SOC_RANGE,
  TEMPERATURE_RANGE,
} from '@/siteview/providers/view-filters';
import { UnreachableCaseError } from '@/utils/errors/unreachable-case-error';

import { alertStatusColor } from '../../colors/alert-status-color';
import { socColor } from '../../colors/soc-color';
import { socOffsetColor } from '../../colors/soc-offset-color';

// Custom inRange function that is inclusive for both start and end values
function inRange(value: number, start: number, end: number): boolean {
  return value >= start && value <= end;
}

export function getSiteviewDynamicData(
  assets: Asset[],
  staticData: StaticData,
  assetsFilters: AssetsFilters,
  viewFilters: ViewFilters,
  __: Settings,
): TDynamicData {
  const elementOpacities: TElementOpacity = elementOpacitiesFromAssetType(
    assetsFilters.assetType,
  );
  const objectStates: Record<string, TObjectState> = {};

  // Parents as ghosts
  {
    const parentIds =
      ParentIds.fromAssetType[assetsFilters.assetType](staticData);

    for (const parentId of parentIds) {
      objectStates[parentId] = {
        color: defaultColor,
        // opacity: 0, // Make them invisible
      };
    }
  }

  // Assets
  {
    for (const asset of assets) {
      if (!showAsset(asset, viewFilters)) {
        continue;
      }

      const color = colorAsset(asset, viewFilters);

      objectStates[asset.id] = {
        color,
        // opacity: 1,
      };
    }
  }

  return {
    objectStates,
    elementOpacities,
  };
}

function elementOpacitiesFromAssetType(assetType: AssetType): TElementOpacity {
  switch (assetType) {
    case AssetType.PCS: {
      return {
        // global
        connection: 1,
        door: 1,
        vent: 1,
        // assets
        pcs: 1,
        container: 0,
        string: 0,
        rack: 0,
        module: 0,
      };
    }
    case AssetType.CONTAINER: {
      return {
        // global
        connection: 1,
        door: 1,
        vent: 1,
        // assets
        pcs: 1,
        container: 1,
        string: 0,
        rack: 0,
        module: 0,
      };
    }
    case AssetType.STRING: {
      return {
        // global
        connection: 1,
        door: 1,
        vent: 1,
        // assets
        pcs: 0,
        container: 0,
        string: 1,
        rack: 0,
        module: 0,
      };
    }
    case AssetType.RACK: {
      return {
        // global
        connection: 1,
        door: 1,
        vent: 1,
        // assets
        pcs: 0,
        container: 0,
        string: 0,
        rack: 1,
        module: 0,
      };
    }
    case AssetType.MODULE: {
      return {
        // global
        connection: 1,
        door: 1,
        vent: 1,
        // assets
        pcs: 0,
        container: 0,
        string: 0,
        rack: 0,
        module: 1,
      };
    }
    default: {
      throw new UnreachableCaseError(assetType);
    }
  }
}

// Determines if an asset should be shown based on the current view filters.
function showAsset(asset: Asset, viewFilters: ViewFilters): boolean {
  switch (viewFilters.viewMode) {
    case ViewFiltersMode.REALISTIC: {
      // In realistic mode, show all assets
      return true;
    }
    case ViewFiltersMode.ACCURE_SOC: {
      // Only show assets with ACCURE SOC values within the specified range
      if (typeof asset.accureSoc !== 'number') {
        return false;
      }

      return inRange(
        asset.accureSoc,
        viewFilters.accureSoc.min,
        viewFilters.accureSoc.max >= ACCURE_SOC_RANGE.max
          ? Infinity
          : viewFilters.accureSoc.max,
      );
    }
    case ViewFiltersMode.BMS_SOC: {
      // Only show assets with BMS SOC values within the specified range
      if (typeof asset.bmsSoc !== 'number') {
        return false;
      }

      return inRange(
        asset.bmsSoc,
        viewFilters.bmsSoc.min,
        viewFilters.bmsSoc.max >= BMS_SOC_RANGE.max
          ? Infinity
          : viewFilters.bmsSoc.max,
      );
    }
    case ViewFiltersMode.ALERT_STATUS: {
      // Show all assets in alert status mode
      return true;
    }
    case ViewFiltersMode.SOC_OFFSET: {
      // Only show assets that have an SOC offset value
      const socOffset = assetSocOffset(asset);
      if (typeof socOffset !== 'number') {
        return false;
      }

      return true;
    }
    case ViewFiltersMode.IMBALANCE_SOC: {
      // Only show assets that have an imbalance SOC value
      if (typeof asset.imbalanceSoc !== 'number') {
        return false;
      }

      return inRange(
        asset.imbalanceSoc,
        viewFilters.imbalanceSoc.min,
        viewFilters.imbalanceSoc.max >= IMBALANCE_SOC_RANGE.max
          ? Infinity
          : viewFilters.imbalanceSoc.max,
      );
    }
    case ViewFiltersMode.TEMPERATURE: {
      // Only show assets that have a temperature value
      if (typeof asset.temperature !== 'number') {
        return false;
      }

      return inRange(
        asset.temperature,
        viewFilters.temperature.min,
        viewFilters.temperature.max >= TEMPERATURE_RANGE.max
          ? Infinity
          : viewFilters.temperature.max,
      );
    }
    default: {
      throw new UnreachableCaseError(viewFilters.viewMode);
    }
  }
}

// Determines the color of an asset based on the current view mode and asset properties.
function colorAsset(asset: Asset, viewFilters: ViewFilters): string {
  switch (viewFilters.viewMode) {
    case ViewFiltersMode.REALISTIC: {
      return defaultColor;
    }
    case ViewFiltersMode.ACCURE_SOC: {
      if (typeof asset.accureSoc !== 'number') {
        return defaultColor;
      }

      return socColor(asset.accureSoc);
    }
    case ViewFiltersMode.BMS_SOC: {
      if (typeof asset.bmsSoc !== 'number') {
        return defaultColor;
      }

      return socColor(asset.bmsSoc);
    }
    case ViewFiltersMode.ALERT_STATUS: {
      return alertStatusColor(asset.alertStatus);
    }
    case ViewFiltersMode.SOC_OFFSET: {
      const socOffset = assetSocOffset(asset);

      if (typeof socOffset !== 'number') {
        return defaultColor;
      }

      return socOffsetColor(socOffset);
    }
    case ViewFiltersMode.IMBALANCE_SOC: {
      if (typeof asset.imbalanceSoc !== 'number') {
        return defaultColor;
      }

      return imbalanceSocColor(asset.imbalanceSoc);
    }
    case ViewFiltersMode.TEMPERATURE: {
      if (typeof asset.temperature !== 'number') {
        return defaultColor;
      }

      return temperatureColor(asset.temperature);
    }
    default: {
      throw new UnreachableCaseError(viewFilters.viewMode);
    }
  }
}

namespace ParentIds {
  const pcs = flow(getOr([], 'pcs'));
  const containers = flow(getOr([], 'containers'));
  const rackStrings = flow(containers, flatMap(getOr([], 'rackStrings')));
  const racks = flow(containers, flatMap(getOr([], 'racks')));

  const getParentIds = <F extends (...args: any[]) => unknown>(
    ...getters: F[]
  ) => flow(over(getters), flatMap(identity), map('uid'), compact);

  export const fromAssetType: Record<
    AssetType,
    (staticData: StaticData) => string[]
  > = {
    [AssetType.PCS]: () => [],
    [AssetType.CONTAINER]: () => [],
    [AssetType.STRING]: getParentIds(pcs, containers),
    [AssetType.RACK]: getParentIds(pcs, containers, rackStrings),
    [AssetType.MODULE]: getParentIds(pcs, containers, rackStrings, racks),
  };
}
